import React from "react"
import { useApp } from "../context/AppContext"
import { Icon } from "../shared"
import { useIsMobile } from "../shared/useIsMobile"
import BrandLogo, { useOrgName } from "../shared/BrandLogo"
import { Btn, Badge } from "../shared/components"
import { markOnboarded } from "../api/programs.api"
import { programAnalytics } from "../api/users.api"
import { tintFg } from '../lib/tint'


/* ============================================================
   Welcome screens — shown once on first login
   ParticipantWelcomeScreen  — journey roadmap + quickstart
   AdminWelcomeScreen        — portfolio + quick actions
   ============================================================ */

// ── Shared blueprint backdrop ────────────────────────────────
function BlueprintBackdrop() {
  return (
    <>
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0, opacity: 0.5, pointerEvents: "none" }}>
        <defs>
          <pattern id="welcome-grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M48 0H0V48" fill="none" stroke="var(--border-subtle)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#welcome-grid)" />
      </svg>
      <div style={{
        position: "absolute", top: "-20%", right: "-10%",
        width: "60%", height: "60%",
        background: "radial-gradient(circle, var(--program-color-soft, rgba(184,78,42,0.08)) 0%, transparent 70%)",
        pointerEvents: "none", opacity: 0.6,
      }} />
      <div style={{
        position: "absolute", bottom: "-30%", left: "-15%",
        width: "50%", height: "60%",
        background: "radial-gradient(circle, rgba(238,155,0,0.06) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
      {/* Decorative compass */}
      <svg width="280" height="280" style={{ position: "absolute", top: 80, right: -60, opacity: 0.05, pointerEvents: "none" }} viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="80" fill="none" stroke="var(--text)" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="60" fill="none" stroke="var(--text)" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="40" fill="none" stroke="var(--text)" strokeWidth="0.5" />
        {Array.from({ length: 16 }).map((_, i) => {
          const a = (i * Math.PI * 2) / 16
          return <line key={i}
            x1={100 + Math.cos(a) * 60} y1={100 + Math.sin(a) * 60}
            x2={100 + Math.cos(a) * 80} y2={100 + Math.sin(a) * 80}
            stroke="var(--text)" strokeWidth={i % 4 === 0 ? 1 : 0.4} />
        })}
        <path d="M100 30 L110 100 L100 90 L90 100 Z" fill="var(--text)" />
      </svg>
    </>
  )
}

// ── Wave top-bar ─────────────────────────────────────────────
function WelcomeTopBar({ subtitle, onSkip, lang }) {
  const isMobile = useIsMobile()
  const { branding } = useApp()
  return (
    <div style={{
      position: "relative",
      display: "flex", alignItems: "center", gap: 12,
      padding: isMobile ? "16px 20px" : "24px 48px",
    }}>
      {/* Logo mark */}
      <BrandLogo size={32} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{branding?.institution_name || 'Gladhy'}</div>
        <div className="muted" style={{ fontSize: 11 }}>{subtitle}</div>
      </div>
      <div style={{ flex: 1 }} />
      <Btn variant="ghost" size="sm" onClick={onSkip}>
        {lang === "id" ? "Lewati →" : "Skip intro →"}
      </Btn>
    </div>
  )
}

// ── CTA gradient bar ─────────────────────────────────────────
function CTABar({ title, sub, btnLabel, onClick }) {
  return (
    <div style={{
      marginTop: 56, padding: "20px 24px",
      background: "linear-gradient(135deg, var(--program-color-ink, #4D2112), var(--program-color-deep, #903D21))",
      color: "white", borderRadius: 16,
      display: "flex", alignItems: "center", gap: 20,
      position: "relative", overflow: "hidden",
    }}>
      <svg viewBox="0 0 800 80" style={{ position: "absolute", inset: 0, opacity: 0.15 }} preserveAspectRatio="none">
        <path d="M0 40 Q 200 10, 400 40 T 800 40" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M0 55 Q 200 25, 400 55 T 800 55" fill="none" stroke="white" strokeWidth="1" opacity="0.6" />
      </svg>
      <div style={{ flex: 1, position: "relative" }}>
        <div style={{ fontSize: 18, fontWeight: 500, letterSpacing: "-0.01em" }}>{title}</div>
        <div style={{ fontSize: 13, opacity: 0.75, marginTop: 2 }}>{sub}</div>
      </div>
      <button
        onClick={onClick}
        style={{
          height: 40, padding: "0 20px", borderRadius: 8,
          background: "white", color: "var(--program-color-deep, #903D21)",
          border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
          position: "relative",
        }}
      >
        {btnLabel}
        <Icon name="chevron_right" size={14} />
      </button>
    </div>
  )
}

// ── Participant Welcome ──────────────────────────────────────
export function ParticipantWelcomeScreen() {
  const { lang, navigate, user, currentProgram, currentProgramId } = useApp()
  const isMobile = useIsMobile()

  // FR-30: any exit from the Welcome screen marks the enrollment onboarded so
  // it never re-appears for this program. Non-fatal — navigation proceeds even
  // if the PATCH fails (the screen is informational, not a hard gate).
  const exitOnboarding = React.useCallback(
    async (to) => {
      try {
        if (currentProgramId) await markOnboarded(currentProgramId)
      } catch { /* non-fatal */ }
      navigate(to)
    },
    [currentProgramId, navigate]
  )

  const steps = [
    { n: 1, t: lang === "id" ? "Pretest" : "Pretest", d: lang === "id" ? "Pengukuran awal" : "Baseline assessment", active: true, icon: "check" },
    { n: 2, t: lang === "id" ? "Belajar" : "Learn", d: lang === "id" ? "Modul & pelajaran program" : "Program modules & lessons", icon: "book" },
    { n: 3, t: lang === "id" ? "Sesi" : "Sessions", d: lang === "id" ? "Kuliah & latihan" : "Lectures & drills", icon: "calendar" },
    { n: 4, t: lang === "id" ? "Posttest" : "Posttest", d: lang === "id" ? "Skor ≥ 70 untuk lulus" : "Score ≥ 70 to qualify", icon: "cert" },
    { n: 5, t: lang === "id" ? "Sertifikat" : "Certify", d: lang === "id" ? "Sertifikasi IMO" : "IMO-aligned credential", icon: "shield" },
  ]

  const quickStart = [
    {
      n: "01",
      t: lang === "id" ? "Ikuti pretest" : "Take your pretest",
      d: lang === "id" ? "30 menit penilaian dasar. Ini hanya mengukur titik awal Anda." : "A 30-minute baseline. It just shows your starting point.",
      cta: lang === "id" ? "Mulai pretest" : "Start pretest",
      primary: true,
      action: () => exitOnboarding("pretest"),
    },
    {
      n: "02",
      t: lang === "id" ? "Mulai belajar" : "Start learning",
      d: lang === "id" ? "Akses kursus dan modul di dashboard Anda." : "Access your courses and modules from the dashboard.",
      cta: lang === "id" ? "Lihat kursus" : "View courses",
      action: () => exitOnboarding("courses"),
    },
    {
      n: "03",
      t: lang === "id" ? "RSVP sesi pertama" : "RSVP to your first session",
      d: lang === "id" ? "Lihat jadwal sesi pelatihan mendatang." : "See upcoming training session schedule.",
      cta: lang === "id" ? "Lihat sesi" : "View sessions",
      action: () => exitOnboarding("sessions"),
    },
  ]

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", position: "relative", overflow: "hidden" }}>
      <BlueprintBackdrop />
      <WelcomeTopBar
        subtitle="Oil Spill Combat Training"
        onSkip={() => exitOnboarding("dashboard")}
        lang={lang}
      />

      <div style={{ position: "relative", maxWidth: 1080, margin: "0 auto", padding: isMobile ? "24px 20px 60px" : "40px 48px 80px" }}>
        {/* Hero */}
        <div style={{ maxWidth: 720 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "5px 12px",
            background: "var(--program-color-soft, rgba(184,78,42,0.08))",
            color: "var(--program-color-text, #903D21)",
            borderRadius: 999,
            fontSize: 11, textTransform: "uppercase",
            letterSpacing: "0.08em", fontWeight: 500,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "var(--program-color, #B84E2A)",
            }} />
            {lang === "id" ? "Anda terdaftar · Program aktif" : "You're enrolled · Program active"}
          </div>

          <h1 style={{
            fontSize: "clamp(48px, 7vw, 80px)",
            fontWeight: 700, lineHeight: 1.05,
            letterSpacing: "-0.03em", marginTop: 28,
          }}>
            {lang === "id" ? "Selamat datang," : "Welcome aboard,"}<br />
            <span style={{ color: "var(--program-color-text, #903D21)" }}>
              {user?.fullName?.split(" ")[0] || (lang === "id" ? "Peserta" : "Trainee")}.
            </span>
          </h1>

          <p style={{ fontSize: 18, color: "var(--text-2, var(--text-secondary))", lineHeight: 1.6, marginTop: 24, maxWidth: 580 }}>
            {lang === "id"
              ? `Anda bergabung di ${currentProgram?.name || "program ini"}. Selesaikan pelajaran, hadiri sesi, dan raih sertifikasi yang diakui industri maritim Indonesia.`
              : `You've joined ${currentProgram?.name || "this program"}. Complete lessons, attend sessions, and earn a certification recognized across Indonesia's maritime industry.`}
          </p>
        </div>

        {/* Journey roadmap */}
        <div style={{ marginTop: 64 }}>
          <div className="eyebrow">{lang === "id" ? "Perjalanan pelatihan Anda" : "Your training journey"}</div>
          <div style={{ position: "relative", marginTop: 24 }}>
            <div style={{
              position: "absolute", top: 24, left: "4%", right: "4%",
              height: 2, background: "repeating-linear-gradient(to right, var(--border-strong) 0 6px, transparent 6px 12px)",
              zIndex: 0,
            }} />
            <div style={{
              position: "absolute", top: 24, left: "4%", width: "12%",
              height: 2, background: "var(--program-color, #B84E2A)", zIndex: 1,
            }} />
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 16, position: "relative", zIndex: 2 }}>
              {steps.map((s) => (
                <div key={s.n} style={{ textAlign: "center" }}>
                  <div style={{
                    width: 50, height: 50, borderRadius: "50%",
                    margin: "0 auto",
                    background: s.active ? "var(--program-color, #B84E2A)" : "var(--surface)",
                    color: s.active ? "white" : "var(--text-tertiary)",
                    border: s.active
                      ? "4px solid var(--program-color-soft, rgba(var(--program-color-rgb), 0.15))"
                      : "1.5px solid var(--border-strong)",
                    display: "grid", placeItems: "center",
                    boxShadow: s.active ? "0 6px 20px -4px rgba(var(--program-color-rgb), 0.4)" : "none",
                  }}>
                    <Icon name={s.icon} size={18} color={s.active ? "white" : "var(--text-tertiary)"} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: s.active ? "var(--program-color-text, #903D21)" : "var(--text)",
                    }}>{s.t}</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.4 }}>{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quickstart + Things to know */}
        <div style={{ marginTop: 64, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 32 }}>
          <div>
            <div className="eyebrow">{lang === "id" ? "Tiga langkah pertama" : "Your first three steps"}</div>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              {quickStart.map((s, i) => (
                <div key={i} style={{
                  padding: "18px 20px",
                  background: "var(--surface)",
                  border: s.primary ? "none" : "1px solid var(--border-subtle)",
                  borderLeft: s.primary ? "4px solid var(--program-color, #B84E2A)" : "1px solid var(--border-subtle)",
                  borderRadius: 14,
                  display: "flex", gap: 16, alignItems: "center",
                  boxShadow: s.primary ? "0 8px 24px -8px rgba(var(--program-color-rgb), 0.25)" : "var(--shadow-xs)",
                }}>
                  <div style={{
                    fontSize: 28, color: "var(--text-tertiary)",
                    fontWeight: 400, fontVariantNumeric: "tabular-nums",
                    minWidth: 36, flexShrink: 0,
                  }}>{s.n}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{s.t}</div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 3, lineHeight: 1.5 }}>{s.d}</div>
                  </div>
                  <Btn variant={s.primary ? "primary" : "secondary"} size="sm" onClick={s.action}>
                    {s.cta}
                  </Btn>
                </div>
              ))}
            </div>
          </div>

          {/* Things to know */}
          <div>
            <div className="eyebrow" style={{ marginBottom: 14 }}>{lang === "id" ? "Perlu diketahui" : "Things to know"}</div>
            <div style={{
              padding: 20, background: "var(--surface)",
              border: "1px solid var(--border-subtle)", borderRadius: 14,
              boxShadow: "var(--shadow-sm)",
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { icon: "clock", t: lang === "id" ? "Belajar mandiri" : "Self-paced learning", d: lang === "id" ? "Tonton kapan saja. Kami melanjutkan dari mana Anda berhenti." : "Watch anytime. We'll resume where you left off." },
                  { icon: "map_pin", t: lang === "id" ? "Sesi geo-fence" : "Geo-fenced sessions", d: lang === "id" ? "Check-in via QR di venue. Dalam radius 50 m dianggap hadir." : "Check in via QR at venue. Within 50 m radius counts." },
                  { icon: "ai", t: lang === "id" ? "Asisten AI" : "AI Assistant on call", d: lang === "id" ? "Tanya apa saja — jawaban dari basis pengetahuan program, lengkap dengan kutipan." : "Ask anything — answers from your program's KB, with citations." },
                  { icon: "globe", t: lang === "id" ? "Subtitle terjemahan live" : "Live translated subtitles", d: lang === "id" ? "Saat sesi berlangsung, ucapan trainer diterjemahkan real-time menjadi subtitle ID/EN — pilih bahasa Anda." : "During a live session, the trainer's speech is translated into real-time ID/EN subtitles — pick your language." },
                  { icon: "shield", t: lang === "id" ? "Privasi terjaga" : "Privacy by default", d: lang === "id" ? "Feedback dianonimkan. Log audit disimpan hanya untuk kepatuhan." : "Feedback is anonymized. Audit logs kept for compliance only." },
                ].map((x, i) => (
                  <div key={i} className="row gap-3" style={{ alignItems: "flex-start" }}>
                    <span style={{
                      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                      background: "var(--program-color-soft, rgba(184,78,42,0.08))",
                      color: "var(--program-color-text, #903D21)",
                      display: "grid", placeItems: "center",
                    }}>
                      <Icon name={x.icon} size={14} color="var(--program-color-text, #903D21)" />
                    </span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{x.t}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{x.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <CTABar
          title={lang === "id" ? "Siap memulai?" : "Ready to begin?"}
          sub={lang === "id" ? "Pretest membutuhkan sekitar 30 menit. Anda bisa menjeda kapan saja." : "Your pretest takes about 30 minutes. You can pause anytime."}
          btnLabel={lang === "id" ? "Masuk dashboard" : "Enter dashboard"}
          onClick={() => exitOnboarding("dashboard")}
        />
      </div>
    </div>
  )
}

// ── Admin/Trainer Welcome ────────────────────────────────────
export function AdminWelcomeScreen() {
  const orgName = useOrgName()
  const { lang, navigate, user, programs, currentProgram, role } = useApp()
  const isMobile = useIsMobile()
  const isTrainer = role === "trainer"

  const [analyticsByProgram, setAnalyticsByProgram] = React.useState({})
  const [loadingStats, setLoadingStats] = React.useState(true)

  React.useEffect(() => {
    if (!programs || programs.length === 0) {
      setLoadingStats(false)
      return
    }
    Promise.all(
      programs.map((p) =>
        programAnalytics(p.id)
          .then((data) => [p.id, data])
          .catch(() => [p.id, null])
      )
    )
      .then((entries) => {
        setAnalyticsByProgram(Object.fromEntries(entries))
      })
      .catch(() => {})
      .finally(() => setLoadingStats(false))
  }, [programs])

  const totalPending = Object.values(analyticsByProgram).reduce((sum, a) => sum + (a?.certPending || 0), 0)
  const programsNeedEnrollment = Object.values(analyticsByProgram).filter((a) => a && (a.total === 0 || !a.total)).length

  const adminActions = [
    { icon: "upload", t: lang === "id" ? "Batch enroll" : "Batch enroll", d: lang === "id" ? "Upload CSV untuk mendaftarkan banyak peserta sekaligus." : "Upload a CSV to enroll many participants at once.", hot: true, action: () => navigate("enrollment") },
    { icon: "calendar", t: lang === "id" ? "Jadwalkan sesi" : "Schedule sessions", d: lang === "id" ? "Buat kuliah, latihan, atau seri berulang." : "Create lectures, drills, or recurring series.", action: () => navigate("training") },
    { icon: "globe", t: lang === "id" ? "Kontrol live" : "Live control", d: lang === "id" ? "Live translation, subtitle, dan analitik sesi." : "Live translation, subtitles, and session analytics.", fresh: true, action: () => navigate("training", { tab: "live" }) },
    { icon: "cert", t: lang === "id" ? "Desain Template" : "Design Template", d: lang === "id" ? "Desain tata letak visual sertifikat untuk setiap program." : "Design the visual layout of certificates for each program.", action: () => navigate("certificates") },
    { icon: "send", t: lang === "id" ? "Kirim Kode Klaim" : "Send Claim Codes", d: lang === "id" ? "Kirimkan kode unik klaim sertifikat ke peserta yang lulus." : "Send unique certificate claim codes to eligible participants.", action: () => navigate("cert-send") },
    { icon: "users", t: lang === "id" ? "Kelola peserta" : "Manage participants", d: lang === "id" ? "Lihat progress, kehadiran, dan skor tiap peserta." : "View progress, attendance, and scores per participant.", action: () => navigate("users") },
    { icon: "chart", t: lang === "id" ? "Buat laporan" : "Build a report", d: lang === "id" ? "Progress · kehadiran · learning gain." : "Progress · attendance · learning gain.", action: () => navigate("reports") },
    { icon: "edit", t: lang === "id" ? "Edit assessment" : "Edit assessments", d: lang === "id" ? "Pretest dan posttest." : "Pretest and posttest.", action: () => navigate("test-editor") },
    { icon: "send", t: lang === "id" ? "Kirim broadcast" : "Send broadcast", d: lang === "id" ? "Notifikasi program atau peserta via email." : "Notify a program or participants by email.", action: () => navigate("emails") },
    { icon: "upload", t: lang === "id" ? "Upload ke KB" : "Upload to KB", d: lang === "id" ? "Tambah dokumen referensi ke Asisten AI." : "Add reference docs to the AI Assistant.", action: () => navigate("kb") },
  ]

  const trainerActions = [
    { icon: "calendar", t: lang === "id" ? "Lihat sesi" : "View sessions", d: lang === "id" ? "Jadwal sesi dan kehadiran peserta." : "Session schedule and participant attendance.", hot: true, action: () => navigate("training") },
    { icon: "globe", t: lang === "id" ? "Kontrol live" : "Live control", d: lang === "id" ? "Live translation, subtitle, dan alat bantu sesi." : "Live translation, subtitles, and session tools.", fresh: true, action: () => navigate("training", { tab: "live" }) },
    { icon: "book", t: lang === "id" ? "Kursus" : "Courses", d: lang === "id" ? "Materi kursus yang Anda ampu." : "The course materials you manage.", action: () => navigate("courses") },
    { icon: "chart", t: lang === "id" ? "Learning Gain" : "Learning Gain", d: lang === "id" ? "Progres dan skor peserta di program Anda." : "Participant progress and scores in your program.", action: () => navigate("learning-gain") },
    { icon: "edit", t: lang === "id" ? "Editor Tes" : "Test Editor", d: lang === "id" ? "Buat dan edit soal pretest/posttest." : "Create and edit pretest/posttest questions.", action: () => navigate("test-editor") },
    { icon: "send", t: lang === "id" ? "Email Peserta" : "Email Participants", d: lang === "id" ? "Kirim pengumuman atau reminder ke peserta." : "Send announcements or reminders to participants.", action: () => navigate("emails") },
    { icon: "play", t: lang === "id" ? "Upload Video" : "Upload Video", d: lang === "id" ? "Tambah video pembelajaran ke kursus." : "Add learning videos to courses.", action: () => navigate("videos") },
    { icon: "doc", t: lang === "id" ? "Knowledge Base" : "Knowledge Base", d: lang === "id" ? "Upload dokumen referensi untuk AI Assistant." : "Upload reference docs for the AI Assistant.", action: () => navigate("kb") },
  ]

  // Trainer tanpa program sama sekali (belum di-assign) tidak punya apa pun
  // untuk ditampilkan di dashboard — ProgramSelectorScreen sudah punya layar
  // khusus untuk itu ("Belum ada program, hubungi admin"). Sebelum ini, ketiga
  // tombol di layar sambutan menulis navigate("dashboard") langsung, memotong
  // keputusan pickLandingPage di AppContext dan mendaratkan orang itu di
  // dashboard kosong tanpa keterangan sama sekali.
  //
  // Admin TIDAK ikut aturan ini — pickLandingPage sengaja mengirim admin ke
  // dashboard apa pun jumlah program di tenant-nya (mis. lembaga baru yang
  // belum membuat satu program pun), sebab admin-lah yang justru membuat
  // program pertama itu dari dashboard. "Belum ada program, hubungi admin"
  // tidak masuk akal ditujukan kepada admin itu sendiri.
  const gotoDashboard = () =>
    navigate(isTrainer && (!programs || programs.length === 0) ? "program-selector" : "dashboard")

  const quickActions = isTrainer ? trainerActions : adminActions
  const greetingName = user?.fullName?.split(" ")[0] || (isTrainer ? "Trainer" : (lang === "id" ? "Admin" : "Admin"))
  const hour = new Date().getHours()
  const greeting = hour < 12
    ? (lang === "id" ? "Selamat pagi" : "Good morning")
    : hour < 17
      ? (lang === "id" ? "Selamat siang" : "Good afternoon")
      : (lang === "id" ? "Selamat malam" : "Good evening")

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", position: "relative", overflow: "hidden" }}>
      <BlueprintBackdrop />
      <WelcomeTopBar
        subtitle={isTrainer ? (lang === "id" ? "Trainer console" : "Trainer console") : "Program admin console"}
        onSkip={gotoDashboard}
        lang={lang}
      />

      <div style={{ position: "relative", maxWidth: 1180, margin: "0 auto", padding: isMobile ? "24px 20px 60px" : "40px 48px 80px" }}>
        {/* Hero */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: isMobile ? 32 : 64, alignItems: "flex-end" }}>
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "5px 12px",
              background: "rgba(var(--program-color-rgb), 0.08)",
              color: "var(--program-color-text, #903D21)",
              border: "1px solid var(--program-color-soft, rgba(var(--program-color-rgb), 0.15))",
              borderRadius: 999,
              fontSize: 11, textTransform: "uppercase",
              letterSpacing: "0.08em", fontWeight: 500,
            }}>
              {isTrainer
                ? (lang === "id" ? `Trainer · ${programs?.length || 0} program` : `Trainer · ${programs?.length || 0} programs`)
                : (lang === "id" ? `Program Admin · ${programs?.length || 0} program` : `Program Admin · ${programs?.length || 0} programs`)}
            </div>

            <h1 style={{
              fontSize: "clamp(40px, 6vw, 72px)",
              fontWeight: 700, lineHeight: 1.05,
              letterSpacing: "-0.03em", marginTop: 24,
            }}>
              {greeting},<br />
              <span style={{ color: "var(--program-color-text, #903D21)" }}>
                {greetingName}.
              </span>
            </h1>

            <p style={{ fontSize: 17, color: "var(--text-2, var(--text-secondary))", lineHeight: 1.6, marginTop: 22, maxWidth: 540 }}>
              {isTrainer
                ? (lang === "id"
                    ? `Selamat datang di ${orgName}. Berikut alat yang Anda butuhkan untuk mengelola sesi dan peserta hari ini.`
                    : `Welcome to ${orgName}. Here are the tools you need to manage your sessions and participants today.`)
                : (lang === "id"
                    ? "Program pelatihan sedang berjalan. Berikut kondisi konsol Anda — dan tindakan yang perlu dilakukan hari ini."
                    : "Training program cohorts are running. Here's the state of your console — and the actions that need you today.")}
            </p>
          </div>

          {/* At-a-glance card */}
          <div style={{
            padding: 24, background: "var(--surface)",
            border: "1px solid var(--border-subtle)", borderRadius: 16,
            boxShadow: "var(--shadow-md)", position: "relative",
          }}>
            <div className="eyebrow">{lang === "id" ? "Hari ini" : "Today"}</div>
            <div style={{ fontSize: 18, fontWeight: 500, marginTop: 4, lineHeight: 1.4 }}>
              {lang === "id" ? "Aksi yang perlu perhatian" : "Actions needing attention"}
            </div>
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Baris "Sesi butuh konfirmasi" dihapus: nilainya dipatok 0 di
                  kode, tanpa API maupun perhitungan apa pun, sehingga selamanya
                  menampilkan nol. Tidak ada konsep konfirmasi sesi di API mana
                  pun — mengadakannya berarti fitur baru, sedangkan
                  membiarkannya berarti memajang angka yang tak pernah benar di
                  kartu yang justru berjudul "perlu perhatian".

                  Label kedua juga diperbaiki: yang dihitung adalah jumlah
                  PROGRAM yang belum punya peserta, bukan jumlah peserta. */}
              {[
                { l: lang === "id" ? "Eligible · sertifikat belum diterbitkan" : "Eligible · cert not issued", tone: "brand", val: totalPending },
                { l: lang === "id" ? "Program tanpa peserta" : "Programs without participants", tone: "warning", val: programsNeedEnrollment },
              ].map((x, i) => (
                <div key={i} className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{x.l}</span>
                  <Badge tone={x.tone}>{loadingStats ? "…" : String(x.val)}</Badge>
                </div>
              ))}
            </div>

            <Btn variant="primary" size="sm" block style={{ marginTop: 16 }} onClick={gotoDashboard}>
              {lang === "id" ? "Buka dashboard" : "Open dashboard"}
            </Btn>
          </div>
        </div>

        {/* Programs portfolio */}
        {programs && programs.length > 0 && (
          <div style={{ marginTop: 72 }}>
            <div className="eyebrow">{lang === "id" ? "Portfolio program" : "Your portfolio"}</div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", marginTop: 4 }}>
              {programs.length} {lang === "id" ? "program" : "programs"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginTop: 20 }}>
              {programs.slice(0, 3).map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate("dashboard")}
                  style={{
                    padding: 22, textAlign: "left",
                    background: "var(--surface)",
                    border: p.id === currentProgram?.id
                      ? `2px solid ${p.color_theme || "var(--program-color)"}`
                      : "1px solid var(--border-subtle)",
                    borderRadius: 14,
                    position: "relative", overflow: "hidden", cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                >
                  {/* Level color strip */}
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 4,
                    background: p.color_theme || "var(--program-color)",
                  }} />
                  <div style={{ marginTop: 4 }}>
                    <div className="row between">
                      <div>
                        <div className="eyebrow" style={{ color: tintFg(p.color_theme || "var(--program-color)", 70) }}>
                          {p.level ? `Level ${p.level}` : p.code?.toUpperCase()}
                        </div>
                        <div className="h3 mt-1">{p.name}</div>
                      </div>
                      {p.id === currentProgram?.id && <Badge tone="brand" dot>{lang === "id" ? "Aktif" : "Active"}</Badge>}
                    </div>
                    <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div className="muted" style={{ fontSize: 12 }}>{lang === "id" ? "Lihat detail" : "View details"}</div>
                      <Icon name="chevron_right" size={14} color="var(--text-tertiary)" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div style={{ marginTop: 56 }}>
          <div className="eyebrow">{lang === "id" ? "Aksi cepat" : "Quick actions"}</div>
          <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em", marginTop: 4 }}>
            {lang === "id" ? "Apa yang ingin Anda lakukan?" : "What would you like to do?"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginTop: 18 }}>
            {quickActions.map((a, i) => (
              <button key={i} onClick={a.action} style={{
                padding: 18, background: "var(--surface)",
                border: "1px solid var(--border-subtle)",
                borderRadius: 12, textAlign: "left", cursor: "pointer",
                transition: "all 0.15s", position: "relative", overflow: "hidden",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.borderColor = "var(--program-color)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--border-subtle)"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: "var(--program-color-soft, rgba(184,78,42,0.08))",
                    color: "var(--program-color-text, #903D21)",
                    display: "grid", placeItems: "center",
                  }}>
                    <Icon name={a.icon} size={16} color="var(--program-color-text, #903D21)" />
                  </span>
                  {a.hot && (
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 9,
                      color: "var(--program-color-text, #903D21)",
                      background: "var(--program-color-soft, rgba(184,78,42,0.08))",
                      padding: "1px 5px", borderRadius: 3, fontWeight: 600,
                    }}>POPULAR</span>
                  )}
                  {a.fresh && (
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 9,
                      color: "white",
                      background: "var(--program-color, #B84E2A)",
                      padding: "1px 5px", borderRadius: 3, fontWeight: 600,
                    }}>{lang === "id" ? "BARU" : "NEW"}</span>
                  )}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 14 }}>{a.t}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{a.d}</div>
              </button>
            ))}
          </div>
        </div>

        <CTABar
          title={isTrainer
            ? (lang === "id" ? "Siap memulai?" : "Ready to start?")
            : (lang === "id" ? "Buka ikhtisar program" : "Open the program overview")}
          sub={isTrainer
            ? (lang === "id" ? "Dashboard trainer menampilkan sesi, peserta, dan progres program Anda." : "The trainer dashboard shows your sessions, participants, and program progress.")
            : (lang === "id" ? "Progress peserta, item tindakan, dan ringkasan program — semuanya dalam satu tampilan." : "Participant progress, action items, and program summary — all in one view.")}
          btnLabel={lang === "id" ? "Masuk dashboard" : "Enter dashboard"}
          onClick={gotoDashboard}
        />
      </div>
    </div>
  )
}
